/*
 * Predictive search for the header search field.
 *
 * <predictive-search> opens its panel when the field gets focus. An empty
 * field shows the server-rendered suggestions (recent searches from this
 * browser, popular searches, popular products). Typing asks Shopify's
 * predictive search after a short pause, as in Horizon: `/search/suggest` with
 * `section_id` returns sections/predictive-search.liquid rendered with the
 * results, whose `predictive-search` region (assets/regions.js) fills the
 * panel. Stale requests are cancelled and answers cached per term. The field
 * is an ARIA combobox: arrow keys move through the panel's options, Enter
 * opens the active one, and Escape closes the panel.
 */

const SEARCH_DELAY = 150;
const RESULT_LIMIT = 6;
const REGION = 'predictive-search';
const RECENT_KEY = 'recent-searches';
const RECENT_LIMIT = 5;

class PredictiveSearch extends HTMLElement {
  #abort;
  #request;
  #timer;
  #cache = new Map();
  #term = '';
  #active = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.form = this.querySelector('form');
    this.input = this.querySelector('input[type="search"]');
    this.panel = this.querySelector('[data-search-panel]');
    this.emptyState = this.querySelector('[data-search-empty]');
    this.results = this.querySelector('[data-search-results]');
    this.recent = this.querySelector('[data-search-recent]');

    this.input.addEventListener('focus', () => this.open(), { signal });
    this.input.addEventListener('click', () => this.open(), { signal });
    this.input.addEventListener('input', this.#onInput, { signal });
    this.input.addEventListener('keydown', this.#onKeydown, { signal });
    this.form.addEventListener('submit', () => this.#remember(this.input.value), { signal });
    this.panel.addEventListener('click', this.#onPanelClick, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    document.addEventListener('pointerdown', (event) => !this.contains(event.target) && this.close(), { signal });
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#request?.abort();
    clearTimeout(this.#timer);
  }

  open() {
    if (this.hasAttribute('open')) return;

    const term = this.input.value.trim();
    if (term) {
      this.#search(term);
    } else {
      this.#showEmptyState();
    }

    this.panel.hidden = false;
    this.toggleAttribute('open', true);
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    if (!this.hasAttribute('open')) return;

    this.panel.hidden = true;
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    this.#setActive(null);
  }

  #onInput = () => {
    clearTimeout(this.#timer);
    this.open();

    const term = this.input.value.trim();
    if (!term) {
      this.#request?.abort();
      this.#term = '';
      this.#showEmptyState();
      return;
    }

    this.#timer = setTimeout(() => this.#search(term), SEARCH_DELAY);
  };

  #onKeydown = (event) => {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault();
        this.open();
        // One step past either end returns to the field itself.
        const options = this.#options();
        const index = options.indexOf(this.#active);
        const step = event.key === 'ArrowDown' ? 1 : -1;
        const next = (index + step + options.length + 2) % (options.length + 1) - 1;
        this.#setActive(options[next] ?? null);
        break;
      }
      case 'Enter':
        if (this.#active) {
          event.preventDefault();
          this.#active.click();
        }
        break;
      case 'Escape':
        // Escape in a search field would also clear it; only close the panel when it's open.
        if (this.hasAttribute('open')) {
          event.preventDefault();
          this.close();
        }
        break;
    }
  };

  // Clicks inside the panel move focus to it (tabindex="-1"), so only focus leaving the component closes it.
  #onFocusOut = (event) => {
    if (!this.contains(event.relatedTarget)) this.close();
  };

  #onPanelClick = (event) => {
    if (event.target.closest('[data-search-clear-recent]')) {
      this.#clearRecent();
      this.input.focus();
      return;
    }

    const link = event.target.closest('a');
    if (!link) return;
    // A picked suggestion, or the typed term that led to a product, becomes a recent search.
    this.#remember(link.dataset.searchTerm ?? this.#term);
  };

  #options() {
    const visible = this.results.hidden ? this.emptyState : this.results;
    return [...visible.querySelectorAll('[role="option"]')].filter((option) => option.offsetParent !== null);
  }

  #setActive(option) {
    this.#active?.removeAttribute('aria-selected');
    this.#active = option;

    if (option) {
      option.setAttribute('aria-selected', 'true');
      option.scrollIntoView({ block: 'nearest' });
      this.input.setAttribute('aria-activedescendant', option.id);
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  #showEmptyState() {
    this.#renderRecent();
    this.#setActive(null);
    this.results.hidden = true;
    this.emptyState.hidden = false;
  }

  async #search(term) {
    this.#term = term;
    this.#request?.abort();

    const cached = this.#cache.get(term);
    if (cached) {
      this.#showResults(cached);
      return;
    }

    const request = new AbortController();
    this.#request = request;
    this.panel.setAttribute('aria-busy', 'true');

    const url = new URL(this.dataset.suggestUrl, window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('section_id', this.dataset.sectionId);
    url.searchParams.set('resources[type]', 'query,product,collection,page,article');
    url.searchParams.set('resources[limit]', String(RESULT_LIMIT));
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag');

    try {
      const regions = await window.themeRegions.load([REGION], { url, signal: request.signal });
      if (!regions.has(REGION)) throw new Error('Predictive search returned no results');
      this.#cache.set(term, regions);
      if (this.#term === term) this.#showResults(regions);
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (this.#term === term) this.#showFallback(term);
    } finally {
      if (this.#request === request) this.panel.removeAttribute('aria-busy');
    }
  }

  #showResults(regions) {
    window.themeRegions.apply(regions);
    this.#setActive(null);
    this.emptyState.hidden = true;
    this.results.hidden = false;
  }

  // Without an answer it doesn't claim there are no results: only the way to the search page, which
  // still works (as does the field as a plain search form).
  #showFallback(term) {
    const region = document.createElement('div');
    const viewAll = document.createElement('a');
    viewAll.className = 'predictive-search__view-all';
    viewAll.setAttribute('role', 'option');
    viewAll.id = 'PredictiveSearchViewAll';
    viewAll.href = this.#searchUrl(term);
    viewAll.dataset.searchTerm = term;
    viewAll.textContent = this.dataset.labelViewAll.replace('[terms]', term);
    region.append(viewAll);
    this.#showResults(new Map([[REGION, region]]));
  }

  #searchUrl(term) {
    const url = new URL(this.dataset.searchUrl, window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('options[prefix]', 'last');
    return url.pathname + url.search;
  }

  // Recent searches live in this browser only; storage can be unavailable (private mode, blocked).
  #recentSearches() {
    try {
      const stored = JSON.parse(window.localStorage.getItem(RECENT_KEY));
      return Array.isArray(stored) ? stored.filter((term) => typeof term === 'string') : [];
    } catch {
      return [];
    }
  }

  #remember(value) {
    const term = value?.trim();
    if (!term) return;

    const terms = [term, ...this.#recentSearches().filter((saved) => saved.toLowerCase() !== term.toLowerCase())];
    try {
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(terms.slice(0, RECENT_LIMIT)));
    } catch {
      // Not saved; recent searches are only a convenience.
    }
  }

  #clearRecent() {
    try {
      window.localStorage.removeItem(RECENT_KEY);
    } catch {
      // Nothing stored to clear.
    }
    this.#renderRecent();
  }

  #renderRecent() {
    if (!this.recent) return;

    const terms = this.#recentSearches();
    const list = this.recent.querySelector('[data-search-recent-list]');
    list.replaceChildren(
      ...terms.map((term, index) => {
        const chip = document.createElement('a');
        chip.className = 'chip';
        chip.setAttribute('role', 'option');
        chip.id = `PredictiveSearchRecent-${index + 1}`;
        chip.href = this.#searchUrl(term);
        chip.dataset.searchTerm = term;
        chip.textContent = term;
        chip.title = term; // shown in full on hover when the chip shortens it
        return chip;
      })
    );
    this.recent.hidden = terms.length === 0;
  }
}

if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
